import {CommonModule} from "@angular/common";
import {HttpClient, HttpParams} from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, FormsModule, ValidationErrors, ValidatorFn} from '@angular/forms';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MatPaginator} from '@angular/material/paginator';
import {MatSelectModule} from "@angular/material/select";
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {LoadingComponent} from "@ui-kit/framework/common/loading.component";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";
import {displayDateWithTime} from "@ui-kit/framework/util/static/date-format-utils";
import {Papa} from 'ngx-papaparse';
import {of, timeout} from "rxjs";
import {catchError} from "rxjs/operators";
import {MaterialModule} from "../../modules/material.module";
import {OtpDialogComponent} from "../otp-dialog/otp-dialog.component";
import {CsvImportReportComponent} from "./csv-import-report/csv-import-report.component";
import {dateTimePattern, UploadDataModel, UploadResponse} from "./csv-import.model";

@Component({
  selector: 'app-csv-import',
  standalone: true,
  imports: [MaterialModule, FormsModule, MatSelectModule, CommonModule],
  templateUrl: './csv-import.component.html',
  styleUrls: ['./csv-import.component.scss']
})
export class CsvImportComponent extends LoadingComponent implements OnInit, OnChanges {

  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  @Input() uploadDataModel: UploadDataModel[] = [];
  @Input() params: { name: string, val: string }[] = [];
  @Input() uploadEndpoint!: string;
  @Input() disableFileInput: boolean = false;
  @Input() disableUpload: boolean = false;
  @Input() disableValidate: boolean = false;

  @Input() otpValidation: boolean = false;
  @Input() otpRequestEndpoint?: string;

  @Input() validateFile: boolean = false;
  @Input() validateEndpoint?: string;

  @Output() dataValidated = new EventEmitter<{req: any[], res: any}>();
  @Output() dataUploaded = new EventEmitter<{req: any[], res: any}>();

  @Output() columnSelected = new EventEmitter<any[]>();

  selectedFileName: string = '';
  originalData: any[] = []; // Store the original CSV data
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]); // Initialize with empty data
  displayedColumns: string[] = [];
  csvColumnHeaders: string[] = [];
  searchText: string = '';
  pageSize: number = 10;
  showColumnMappingForm: boolean = false;

  infoIcon = 'info-icon';
  infoMsg = 'Set up your file to import';
  uploadReport!: UploadResponse;

  // Define a form group for column mapping
  columnMappingForm!: FormGroup;

  otpDialogRef?: MatDialogRef<OtpDialogComponent, any>;

  constructor(
    private papa: Papa,
    private fb: FormBuilder,
    private httpClient: HttpClient,
    private matDialog: MatDialog,
    private snackBar: SnackbarService,
    _loader: LoadingService,
    cdr: ChangeDetectorRef
    ) {
    super(_loader, cdr)
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.resetPage();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (propName !== 'disableUpload') {
        this.resetPage();
      }
    }
  }

  overwriteData(data: any[]) {
    this.dataSource.data = data;
  }

  get formColumns(): UploadDataModel[] {
    return this.uploadDataModel.filter(f => f.selectColumn);
  }

  onFileSelected(event: any) {
    this.infoIcon = 'info-icon';
    this.infoMsg = 'Set up your file to import';
    this.disableValidate = false;

    const file = event.target.files[0];
    this.resetParsedData();
    if (file) {
      this.selectedFileName = file.name;
      this.parseCsv(file);
    }
  }

  parseCsv(file: File) {
    this.papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      transformHeader: (header) => header.trim(),
      complete: (result) => {
        if (result.meta.fields) {
          this.originalData = result.data; // Store the original CSV data
          this.showColumnMappingForm = true; // Show the column mapping form
          this.dataSource = new MatTableDataSource<any>([]); // Initialize with empty data
          this.displayedColumns = this.uploadDataModel
            .filter(f => f.showInTable)
            .map((modelProp) => modelProp.property); // Initialize displayed columns
          this.csvColumnHeaders = result.meta.fields;
        }
      },
    });
  }

  applyFilter() {
    const filterValue = this.searchText.trim().toLowerCase();
    this.dataSource.filter = filterValue;

    // Handle pagination when search filters are applied
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  updateTable() {
    // Reset validation messages
    for (const modelProp of this.uploadDataModel) {
      this.columnMappingForm.get(modelProp.property)?.setErrors(null);
    }

    this.columnSelected.emit(this.mapCsvData(this.originalData));

    // Check if the form is valid before updating the table
    if (this.columnMappingForm.valid) {
      const mappedData = this.mapCsvData(this.originalData);
      this.dataSource = new MatTableDataSource<any>(mappedData);

      setTimeout(() => {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
      }, 1500)

    }
    this.columnMappingForm.updateValueAndValidity();
  }

  private trimIfString(value: any): any {
    if (typeof value === 'string') {
      value = value.trim(); // Trim the value if it's a string
    }
    return value;
  }

  // Function to map CSV data to the specified model
  mapCsvData(data: any[]): any[] {
    const mappings = this.columnMappingForm.value;
    const firstRow = data[0]; // Get the first row of data

    // Check and set validation messages
    for (const modelProp of this.uploadDataModel) {
      const csvColumn = mappings[modelProp.property] ? mappings[modelProp.property].trim() : mappings[modelProp.property]; // Trim leading/trailing spaces
      if (csvColumn) {
        const value = firstRow[csvColumn];
        const isValid = modelProp.validators.every((validator) =>
          validator.test(this.trimIfString(value))
        );
        if (!isValid) {
          // Set validation message for the corresponding select control
          this.columnMappingForm.get(modelProp.property)?.setErrors({
            patternMismatch: true,
          });
        }
      }
    }

    return data.map((row) => {
      const mappedRow: any = {}; // Specify type 'any'
      for (const modelProp of this.uploadDataModel) {
        const csvColumn = mappings[modelProp.property] ? mappings[modelProp.property].trim() : mappings[modelProp.property]; // Trim leading/trailing spaces
        if (modelProp.selectColumn && csvColumn) {
          mappedRow[modelProp.property] = this.trimIfString(row[csvColumn]);
          if (modelProp.validators.includes(dateTimePattern)) {
            mappedRow[modelProp.property] = displayDateWithTime(mappedRow[modelProp.property]);
          }
        } else {
          if (modelProp.computeValue) {
            mappedRow[modelProp.property] = modelProp.computeMethod(mappedRow);
          } else {
            mappedRow[modelProp.property] = mappings[modelProp.property];
          }
        }
      }
      return mappedRow;
    });
  }

  // Function to get the label for a column based on the selected mappings
  getLabelForColumn(column: string): string {
    const mapping = this.uploadDataModel.find(
      (modelProp) => modelProp.property === column
    );
    return mapping ? mapping.label : column;
  }

  validateUploadData() {
    const mappedData = this.mapCsvData(this.originalData);
    this.validateDataUsingEndpoint(mappedData);
  }

  // Function to send data to the API
  uploadData() {
    const mappedData = this.dataSource.data;
    if (this.otpValidation) {
      this.doOtpValidation(mappedData);
    } else {
      this.uploadDataUsingEndpoint(mappedData);
    }
  }

  doOtpValidation(data: any[]) {
    // this will call the upload endpoint anyway
    this.otpDialogRef = this.matDialog.open(OtpDialogComponent, {
      data: {
        message: 'Please confirm you email address to update these banking details.',
        requestEndpoint: this.otpRequestEndpoint,
        confirmEndpoint: this.uploadEndpoint,
        confirmBody: data,
        params: this.params
      }
    })

    this.otpDialogRef.componentInstance.dataSubmitted.subscribe(res => {
      this.dataUploaded.emit({res: res, req: data});
    })
  }

  closeOtpDialog() {
    this.otpDialogRef!.close();
  }

  validateDataUsingEndpoint(mappedData: any[]) {
    let requestParams = new HttpParams();
    const timeoutAmount = 300; // 5min

    this.infoMsg = 'Validating your file...';

    if (this.params) {
      this.params.filter(f => f.val !== null).forEach(ea => {
        requestParams = requestParams.append(ea.name, ea.val)
      });
    }
    this.httpClient.post(this.validateEndpoint!, mappedData, { params: requestParams })
      .pipe(
        timeout(timeoutAmount * 1000),
        catchError(e => {
          console.log(e, timeout)
          return of(null)
        })
      )
      .subscribe({
        next: (value) => {
          this.infoMsg = 'File successfully validated.'
          this.dataValidated.emit({req: mappedData, res: value});
          this.disableValidate = true;
        },
        error: err => {
          this.infoMsg = 'Could not validate file.';
          this.infoIcon = 'error-icon';
        }
      });
  }

  uploadDataUsingEndpoint(mappedData: any[]) {
    let requestParams = new HttpParams();
    const timeoutAmount = 300; // 5min

    this.infoMsg = 'Uploading your file...';

    if (this.params) {
      this.params.filter(f => f.val !== null).forEach(ea => {
        requestParams = requestParams.append(ea.name, ea.val)
      });
    }
    this.httpClient.post(this.uploadEndpoint, mappedData, { params: requestParams })
      .pipe(
        timeout(timeoutAmount * 1000),
        catchError(e => {
          console.log(e, timeout)
          return of(null)
        })
      )
      .subscribe({
        next: (value: any) => {
          this.dataUploaded.emit({req: mappedData, res: value});
          this.fileSuccessfullyUploaded(mappedData, value);
        },
        error: err => {
          this.fileUploadError(err);
        }
      });
  }

  fileSuccessfullyUploaded(mappedData: any[], value: UploadResponse ) {
    console.log('Data sent to the API:', mappedData);
    console.log('Response: ', value);
    this.uploadReport = value;
    this.infoMsg = 'File successfully uploaded, click here to see the results.'
    this.resetPage();
  }

  fileUploadError(err: any) {
    console.log(err);
    this.infoMsg = 'Something went wrong, please check your data!'
    this.infoIcon = 'error-icon'
  }

  resetFileSelection() {
    if (this.fileInput) this.fileInput.nativeElement.value = "";
    this.selectedFileName = '';
    this.paginator?.firstPage();
    this.dataSource = new MatTableDataSource<any>([]);
    this.originalData = [];
  }

  resetParsedData() {
    this.originalData = [];
    this.dataSource = new MatTableDataSource<any>([]);
    this.paginator?.firstPage();
    this.csvColumnHeaders = [];
    this.showColumnMappingForm = false;
  }

  resetPage(): void {
    this.selectedFileName = '';
    this.originalData = [];
    this.dataSource = new MatTableDataSource<any>([]);
    this.paginator?.firstPage();

    // this.displayedColumns: string[] = [];
    this.csvColumnHeaders = [];
    this.searchText = '';
    this.showColumnMappingForm = false;

    if (this.fileInput) this.fileInput.nativeElement.value = "";

    // Define a form group for column mapping
    const initialMappings: any = {};
    for (const modelProp of this.uploadDataModel) {
      initialMappings[modelProp.property] = null;
    }
    this.columnMappingForm = this.fb.group(initialMappings, {
      validators: [this.requiredColumnsValidator()],
    });
    this.showColumnMappingForm = true;
  }

  requiredColumnsValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const invalidColumns: string[] = [];

      for (const modelProp of this.uploadDataModel) {
        if (modelProp.required) {
          const selectedColumn = formGroup.get(modelProp.property)?.value;
          if (!selectedColumn || selectedColumn && selectedColumn.trim() === '') {
            invalidColumns.push(modelProp.property);
          }
        }
      }

      return invalidColumns.length > 0
        ? { requiredColumns: invalidColumns }
        : null;
    };
  }

  displayErrors(data: any) {
    data.forEach((e: string) => this.snackBar.openSnackBar(e));
  }

  openReport() {
    this.matDialog.open(CsvImportReportComponent, {
      panelClass: 'dialog-large',
      data: {
        uploadReport: this.uploadReport
      }
    })
  }
}
